// =============================================================================
// MeuDriver — Student Booking Detail
// =============================================================================

import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom'
import {
  ChevronLeft,
  MapPin,
  Clock,
  Send,
  Star,
  CheckCircle,
  AlertTriangle,
  MessageCircle,
  Calendar,
  XCircle,
  CalendarCheck,
  Flag,
  Printer,
  CalendarPlus,
  FileText,
  BookOpen,
  Paperclip,
  X as XIcon,
  Tag,
  Loader2,
} from 'lucide-react'
import type { DisputeReason } from '@/types/database'

import { useQuery } from '@tanstack/react-query'
import { useBookingInvoice } from '@/hooks/useInvoice'
import { InvoiceDocument, InvoiceDocumentSkeleton } from '@/components/shared/InvoiceDocument'
import { CancellationPolicy } from '@/components/shared/CancellationPolicy'
import { InstructorServiceAreaMap } from '@/components/instructor/InstructorServiceAreaMap'
import { useAuth } from '@/hooks/useAuth'
import { useBooking } from '@/hooks/useBookings'
import { useCancelBooking, useCancellationPreview } from '@/hooks/useCancellation'
import { sendEmailSilent } from '@/hooks/useEmailNotifications'
import { useRealtimeMessages } from '@/hooks/useRealtime'
import { supabase } from '@/lib/supabase'
import { track } from '@/lib/analytics'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Textarea } from '@/components/ui/textarea'
import { Separator } from '@/components/ui/separator'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  formatDateTime,
  formatCurrency,
  getBookingStatusLabel,
  getBookingStatusColor,
  getInitials,
} from '@/lib/utils'
import type { BookingWithDetails, BookingStatus, Message } from '@/types/database'
import { RescheduleRequestPanel } from '@/components/shared/RescheduleRequestPanel'
import { useApplyPromoCode } from '@/hooks/usePromoCodes'
import { format, parseISO, differenceInHours, isFuture } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { DURATION_OPTIONS } from '@/lib/constants'
import { generateICS, downloadICS } from '@/lib/ics'

// ─── Constants ───────────────────────────────────────────────────────────────

const REVIEW_TAGS = [
  'Muito paciente',
  'Pontual',
  'Boa didática',
  'Carro bem cuidado',
  'Recomendo a todos',
  'Ótima comunicação',
  'Explicações claras',
  'Ambiente tranquilo',
]

// ─── Helpers ─────────────────────────────────────────────────────────────────

function canCancel(booking: BookingWithDetails): boolean {
  if (!['confirmed', 'pending_payment'].includes(booking.status)) return false
  const scheduledAt = parseISO(booking.scheduled_at)
  return isFuture(scheduledAt) && differenceInHours(scheduledAt, new Date()) > 2
}

function canReschedule(booking: BookingWithDetails): boolean {
  if (booking.status !== 'confirmed') return false
  const scheduledAt = parseISO(booking.scheduled_at)
  return isFuture(scheduledAt) && differenceInHours(scheduledAt, new Date()) > 24
}

function getDurationLabel(minutes: number): string {
  return DURATION_OPTIONS.find((o) => o.value === minutes)?.label ?? `${minutes} min`
}

// ─── Status Timeline ──────────────────────────────────────────────────────────

const STATUS_TIMELINE: { status: BookingStatus; label: string }[] = [
  { status: 'confirmed', label: 'Confirmada' },
  { status: 'in_progress', label: 'Em andamento' },
  { status: 'completed', label: 'Concluída' },
]

const STATUS_ORDER: Record<string, number> = {
  pending_payment: 0,
  confirmed: 1,
  in_progress: 2,
  completed: 3,
  cancelled_by_student: -1,
  cancelled_by_instructor: -1,
  no_show: -1,
}

interface StatusTimelineProps {
  status: BookingStatus
}

function StatusTimeline({ status }: StatusTimelineProps) {
  const isCancelled = ['cancelled_by_student', 'cancelled_by_instructor', 'no_show'].includes(status)

  if (isCancelled) {
    return (
      <div className="flex items-center gap-2 text-red-600">
        <XCircle className="h-5 w-5" />
        <span className="font-medium text-sm">{getBookingStatusLabel(status)}</span>
      </div>
    )
  }

  const currentOrder = STATUS_ORDER[status] ?? 0

  return (
    <div className="flex items-center gap-0">
      {STATUS_TIMELINE.map((step, idx) => {
        const stepOrder = STATUS_ORDER[step.status] ?? 0
        const isDone = currentOrder > stepOrder
        const isActive = step.status === status

        return (
          <div key={step.status} className="flex items-center">
            <div className="flex flex-col items-center gap-1">
              <div
                className={`h-7 w-7 rounded-full flex items-center justify-center transition-colors
                  ${isDone
                    ? 'bg-green-500 text-white'
                    : isActive
                      ? 'bg-primary text-white ring-2 ring-primary/30'
                      : 'bg-gray-200 text-gray-400'
                  }`}
              >
                {isDone
                  ? <CheckCircle className="h-4 w-4" />
                  : <span className="text-xs font-bold">{idx + 1}</span>
                }
              </div>
              <span className={`text-xs font-medium whitespace-nowrap ${isActive ? 'text-primary' : isDone ? 'text-green-600' : 'text-muted-foreground'}`}>
                {step.label}
              </span>
            </div>
            {idx < STATUS_TIMELINE.length - 1 && (
              <div className={`h-0.5 w-12 sm:w-20 mx-1 mb-5 rounded transition-colors ${isDone ? 'bg-green-400' : 'bg-gray-200'}`} />
            )}
          </div>
        )
      })}
    </div>
  )
}

// ─── Chat Component ───────────────────────────────────────────────────────────

interface ChatProps {
  bookingId: string
  currentUserId: string
}

// ─── Image message bubble ─────────────────────────────────────────────────────

function ImageBubble({ imageUrl }: { imageUrl: string }) {
  const [signedUrl, setSignedUrl] = useState<string | null>(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    // Extrair o path relativo do bucket (após /chat-images/)
    // A URL armazenada é o path no bucket, não uma URL pública.
    supabase.storage
      .from('chat-images')
      .createSignedUrl(imageUrl, 3600)
      .then(({ data, error: err }) => {
        if (err || !data) {
          setError(true)
        } else {
          setSignedUrl(data.signedUrl)
        }
      })
  }, [imageUrl])

  if (error) {
    return (
      <p className="text-xs text-muted-foreground italic">[imagem indisponível]</p>
    )
  }

  if (!signedUrl) {
    return <Skeleton className="h-32 w-40 rounded-lg" />
  }

  return (
    <a href={signedUrl} target="_blank" rel="noopener noreferrer">
      <img
        src={signedUrl}
        alt="Imagem enviada"
        className="max-h-48 max-w-[240px] rounded-lg border object-cover cursor-pointer hover:opacity-90 transition-opacity"
      />
    </a>
  )
}

function ChatSection({ bookingId, currentUserId }: ChatProps) {
  const { messages, sendMessage, isLoading } = useRealtimeMessages(bookingId)
  const [input, setInput] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const imageInputRef = useRef<HTMLInputElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  async function handleSend() {
    const content = input.trim()
    if (!content && !imageFile) return
    setIsSending(true)
    try {
      let uploadedPath: string | null = null

      if (imageFile) {
        const ext = imageFile.name.split('.').pop() ?? 'jpg'
        const path = `${currentUserId}/${crypto.randomUUID()}.${ext}`
        const { error: upErr } = await supabase.storage
          .from('chat-images')
          .upload(path, imageFile, { upsert: false })
        if (upErr) throw upErr
        uploadedPath = path
      }

      // sendMessage espera só content — insert direto para suportar image_url
      const { error: insErr } = await supabase.from('messages').insert({
        booking_id: bookingId,
        sender_id: currentUserId,
        content: content || ' ', // content NOT NULL no schema existente
        image_url: uploadedPath,
      })
      if (insErr) throw insErr

      setInput('')
      setImageFile(null)
      setImagePreview(null)
    } catch {
      toast.error('Erro ao enviar mensagem. Tente novamente.')
    } finally {
      setIsSending(false)
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  function handleImageSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Imagem muito grande. Máximo 5 MB.')
      return
    }
    setImageFile(file)
    setImagePreview(URL.createObjectURL(file))
    if (e.target) e.target.value = ''
  }

  // Group messages by date
  const grouped = messages.reduce<Record<string, Message[]>>((acc, msg) => {
    const day = format(parseISO(msg.created_at), 'yyyy-MM-dd')
    if (!acc[day]) acc[day] = []
    acc[day].push(msg)
    return acc
  }, {})

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <MessageCircle className="h-4 w-4 text-primary" />
          Chat com o instrutor
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <ScrollArea className="h-72 px-4">
          {isLoading ? (
            <div className="space-y-3 py-4">
              <Skeleton className="h-10 w-48 ml-auto rounded-2xl rounded-tr-sm" />
              <Skeleton className="h-10 w-56 rounded-2xl rounded-tl-sm" />
              <Skeleton className="h-10 w-40 ml-auto rounded-2xl rounded-tr-sm" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex items-center justify-center h-full text-muted-foreground text-sm py-8">
              Nenhuma mensagem ainda. Diga olá para o instrutor!
            </div>
          ) : (
            <div className="space-y-4 py-4">
              {Object.entries(grouped).map(([day, dayMessages]) => (
                <div key={day}>
                  {/* Date separator */}
                  <div className="flex items-center gap-2 my-3">
                    <div className="flex-1 h-px bg-gray-200" />
                    <span className="text-xs text-muted-foreground px-2 bg-white">
                      {format(parseISO(day), "d 'de' MMMM", { locale: ptBR })}
                    </span>
                    <div className="flex-1 h-px bg-gray-200" />
                  </div>

                  {/* Messages */}
                  {dayMessages.map((msg) => {
                    const isOwn = msg.sender_id === currentUserId
                    return (
                      <div
                        key={msg.id}
                        className={`flex mb-2 ${isOwn ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm
                            ${isOwn
                              ? 'bg-primary text-white rounded-tr-sm'
                              : 'bg-gray-100 text-gray-900 rounded-tl-sm'
                            }`}
                        >
                          {/* Imagem, se houver */}
                          {msg.image_url && (
                            <div className="mb-1">
                              <ImageBubble imageUrl={msg.image_url} />
                            </div>
                          )}
                          {/* Texto (pode ser só espaço quando é só imagem) */}
                          {msg.content.trim() && (
                            <p className="break-words">{msg.content}</p>
                          )}
                          <p className={`text-xs mt-0.5 ${isOwn ? 'text-primary-foreground/70' : 'text-muted-foreground'} text-right`}>
                            {format(parseISO(msg.created_at), 'HH:mm')}
                          </p>
                        </div>
                      </div>
                    )
                  })}
                </div>
              ))}
              <div ref={bottomRef} />
            </div>
          )}
        </ScrollArea>

        {/* Preview da imagem selecionada */}
        {imagePreview && (
          <div className="px-3 pt-2 flex items-center gap-2">
            <div className="relative">
              <img
                src={imagePreview}
                alt="Preview"
                className="h-14 w-14 rounded-lg object-cover border"
              />
              <button
                type="button"
                onClick={() => { setImageFile(null); setImagePreview(null) }}
                className="absolute -top-1.5 -right-1.5 rounded-full bg-gray-700 p-0.5 text-white"
              >
                <XIcon className="h-3 w-3" />
              </button>
            </div>
            <p className="text-xs text-muted-foreground">Imagem pronta para envio</p>
          </div>
        )}

        {/* Input */}
        <div className="flex gap-2 p-3 border-t">
          {/* Botão de clipe para upload de imagem */}
          <button
            type="button"
            onClick={() => imageInputRef.current?.click()}
            disabled={isSending}
            className="shrink-0 self-end h-10 w-10 flex items-center justify-center rounded-md border border-input bg-background text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50"
            aria-label="Enviar imagem"
            data-testid="chat-image-upload"
          >
            <Paperclip className="h-4 w-4" />
          </button>
          <input
            ref={imageInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={handleImageSelect}
          />
          <Textarea
            rows={1}
            className="resize-none min-h-[40px] max-h-24 flex-1"
            placeholder="Digite uma mensagem..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <Button
            size="icon"
            onClick={handleSend}
            disabled={(!input.trim() && !imageFile) || isSending}
            className="shrink-0 self-end"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

// ─── Star Rating ──────────────────────────────────────────────────────────────

interface StarRatingProps {
  value: number
  onChange: (rating: number) => void
}

function StarRating({ value, onChange }: StarRatingProps) {
  const [hovered, setHovered] = useState(0)

  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          className="focus:outline-none"
          aria-label={`${star} estrela${star > 1 ? 's' : ''}`}
        >
          <Star
            className={`h-8 w-8 transition-colors ${
              (hovered || value) >= star
                ? 'text-orange-400 fill-orange-400'
                : 'text-gray-300 fill-gray-100'
            }`}
          />
        </button>
      ))}
    </div>
  )
}

// ─── Review Section ───────────────────────────────────────────────────────────

interface ReviewSectionProps {
  booking: BookingWithDetails
  studentId: string
  onReviewSubmitted: () => void
}

function ReviewSection({ booking, studentId, onReviewSubmitted }: ReviewSectionProps) {
  const [rating, setRating] = useState(0)
  const [comment, setComment] = useState('')
  const [showName, setShowName] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (rating === 0) {
      toast.error('Por favor, selecione uma avaliação de 1 a 5 estrelas.')
      return
    }
    if (comment.trim().length < 10) {
      toast.error('O comentário deve ter pelo menos 10 caracteres.')
      return
    }

    setSubmitting(true)
    try {
      const { error } = await supabase.from('reviews').insert({
        booking_id: booking.id,
        student_id: studentId,
        instructor_id: booking.instructor_id,
        rating,
        comment: comment.trim(),
        anonymous: !showName,
      })

      if (error) throw new Error(error.message)

      toast.success('Avaliação enviada com sucesso!')
      onReviewSubmitted()

      // Notifica o instrutor via e-mail (best-effort)
      try {
        const { data: instructorProfile } = await supabase
          .from('instructors')
          .select('profile_id, profile:profiles!instructors_profile_id_fkey(full_name)')
          .eq('id', booking.instructor_id)
          .maybeSingle()

        if (instructorProfile?.profile_id) {
          const { data: authData } = await supabase.auth.getSession()
          const jwt = authData.session?.access_token
          if (jwt) {
            // Busca e-mail do instrutor via edge function (o cliente não tem acesso a auth.users)
            // Usa send-email diretamente — a edge function acessa o e-mail internamente
            // Aqui apenas passamos o instructor_id como related_id; a edge function
            // resolve o e-mail se necessário. Para este padrão, usamos o sendEmailSilent
            // passando o instructor_profile_id para identificação.
            // Nota: o envio real de e-mail ao instrutor é tratado pelo mp-webhook e
            // pelo cron de notificações. Aqui disparamos um best-effort.
            await sendEmailSilent({
              to: `instrutor-${instructorProfile.profile_id}@placeholder.invalid`,
              template: 'new_review',
              data: {
                instructor_name: (instructorProfile.profile as { full_name?: string | null } | null)?.full_name ?? 'Instrutor',
                rating,
                comment: comment.trim(),
                related_id: booking.instructor_id,
              },
            })
          }
        }
      } catch {
        // silenciado — best-effort
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao enviar avaliação.'
      toast.error(msg)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Card className="border-orange-200 bg-orange-50/30">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2 text-orange-700">
          <Star className="h-4 w-4" />
          Avaliar esta aula
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <p className="text-sm text-muted-foreground mb-2">Sua avaliação geral:</p>
            <StarRating value={rating} onChange={setRating} />
            {rating > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                {['', 'Muito ruim', 'Ruim', 'Regular', 'Bom', 'Excelente!'][rating]}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">Sugestões rápidas</p>
            <div className="flex flex-wrap gap-2">
              {REVIEW_TAGS.map((tag) => {
                const active = comment.includes(tag)
                return (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => {
                      if (active) {
                        setComment((c) => c.replace(tag, '').replace(/  +/, ' ').trim())
                      } else {
                        setComment((c) => c ? `${c.trimEnd()} ${tag}` : tag)
                      }
                    }}
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${
                      active
                        ? 'bg-orange-100 text-orange-700 border-orange-300'
                        : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-orange-300 hover:text-orange-600'
                    }`}
                  >
                    {active ? '✓ ' : ''}{tag}
                  </button>
                )
              })}
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Comentário
            </label>
            <Textarea
              placeholder="Descreva sua experiência com o instrutor..."
              rows={3}
              maxLength={500}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
            <p className="text-xs text-muted-foreground text-right">{comment.length}/500</p>
          </div>

          <label className="flex items-center justify-between rounded-lg border border-orange-200 bg-white px-3 py-2 cursor-pointer">
            <span className="text-sm text-gray-700">
              Exibir meu nome na avaliação
              <span className="block text-xs text-muted-foreground mt-0.5">
                Desligue para aparecer como "Aluno verificado"
              </span>
            </span>
            <input
              type="checkbox"
              className="h-5 w-9 appearance-none rounded-full bg-gray-200 checked:bg-orange-500 transition-colors relative before:absolute before:top-0.5 before:left-0.5 before:h-4 before:w-4 before:rounded-full before:bg-white before:transition-transform checked:before:translate-x-4"
              checked={showName}
              onChange={(e) => setShowName(e.target.checked)}
            />
          </label>

          <Button
            type="submit"
            disabled={submitting || rating === 0}
            className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600"
          >
            {submitting ? 'Enviando...' : 'Enviar avaliação'}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

// ─── Cancel Dialog ────────────────────────────────────────────────────────────

interface CancelDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  isPending: boolean
  refundAmount?: number
  refundPercentage?: number
  policyName?: string
}

// ─── Dispute Dialog ───────────────────────────────────────────────────────────

const DISPUTE_REASONS: { value: DisputeReason; label: string }[] = [
  { value: 'no_show_instructor',  label: 'Instrutor não compareceu' },
  { value: 'quality_issue',       label: 'Qualidade insatisfatória da aula' },
  { value: 'wrong_amount',        label: 'Valor cobrado incorreto' },
  { value: 'unsafe_behavior',     label: 'Comportamento inadequado do instrutor' },
  { value: 'other',               label: 'Outro motivo' },
]

interface DisputeDialogProps {
  open: boolean
  onClose: () => void
  bookingId: string
  studentId: string
  instructorId: string
}

function DisputeDialog({ open, onClose, bookingId, studentId, instructorId }: DisputeDialogProps) {
  const [reason, setReason] = useState<DisputeReason>('other')
  const [description, setDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const { data: existing } = useQuery({
    queryKey: ['dispute', bookingId],
    queryFn: async () => {
      const { data } = await supabase
        .from('disputes')
        .select('id, status')
        .eq('booking_id', bookingId)
        .maybeSingle()
      return data
    },
    enabled: open,
  })

  async function handleSubmit() {
    if (!description.trim() || description.trim().length < 20) {
      toast.error('Descreva o problema com pelo menos 20 caracteres.')
      return
    }
    setSubmitting(true)
    try {
      await supabase.from('disputes').insert({
        booking_id: bookingId,
        student_id: studentId,
        instructor_id: instructorId,
        reason,
        description: description.trim(),
      }).throwOnError()
      track('dispute_opened', { booking_id: bookingId, reason })
      toast.success('Disputa aberta! Nossa equipe analisará em até 48h.')
      onClose()
    } catch {
      toast.error('Erro ao abrir disputa. Tente novamente.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <Flag className="h-5 w-5" />
            Abrir disputa
          </DialogTitle>
        </DialogHeader>

        {existing ? (
          <div className="space-y-4">
            <div className={`rounded-lg p-4 text-sm ${
              existing.status === 'resolved' ? 'bg-green-50 text-green-800 border border-green-200' :
              existing.status === 'under_review' ? 'bg-blue-50 text-blue-800 border border-blue-200' :
              'bg-amber-50 text-amber-800 border border-amber-200'
            }`}>
              <p className="font-semibold mb-1">
                Disputa {existing.status === 'open' ? 'em aberto' : existing.status === 'under_review' ? 'em análise' : existing.status === 'resolved' ? 'resolvida' : 'encerrada'}
              </p>
              <p className="text-xs">
                {existing.status === 'open' || existing.status === 'under_review'
                  ? 'Nossa equipe está analisando sua disputa e entrará em contato em até 48 horas.'
                  : 'Esta disputa foi encerrada.'}
              </p>
            </div>
            <Button variant="outline" onClick={onClose} className="w-full">Fechar</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700">
              Abrir uma disputa é uma ação séria. Use apenas quando houver um problema real com a aula.
              Nossa equipe irá analisar e tomar as medidas necessárias.
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">Motivo</label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value as DisputeReason)}
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
              >
                {DISPUTE_REASONS.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">Descrição detalhada *</label>
              <Textarea
                placeholder="Descreva o ocorrido com o máximo de detalhes possível (mínimo 20 caracteres)..."
                rows={4}
                maxLength={1000}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              <p className="text-xs text-muted-foreground text-right">{description.length}/1000</p>
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={onClose} disabled={submitting} className="flex-1">
                Cancelar
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting || description.trim().length < 20}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                {submitting ? 'Enviando...' : 'Abrir disputa'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

// ─── Cancel Dialog ────────────────────────────────────────────────────────────

function CancelDialog({
  open, onClose, onConfirm, isPending,
  refundAmount, refundPercentage, policyName,
}: CancelDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-5 w-5" />
            Cancelar aula
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <CancellationPolicy />

          {/* Preview de reembolso calculado */}
          {refundPercentage !== undefined && (
            <div className={`rounded-lg p-3 text-sm ${refundPercentage > 0 ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' : 'bg-amber-50 border border-amber-200 text-amber-800'}`}>
              {policyName && <p className="font-medium mb-0.5">{policyName}</p>}
              {refundPercentage > 0 && refundAmount !== undefined ? (
                <p>
                  Voce receberá <strong>R$ {refundAmount.toFixed(2).replace('.', ',')} de volta</strong> ({refundPercentage}% de reembolso).
                </p>
              ) : (
                <p>Nenhum reembolso disponivel para este cancelamento.</p>
              )}
            </div>
          )}

          <p className="text-sm text-gray-700">
            Tem certeza que deseja cancelar esta aula? Esta ação não pode ser desfeita.
          </p>
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={onClose} disabled={isPending}>
              Manter aula
            </Button>
            <Button
              variant="destructive"
              onClick={onConfirm}
              disabled={isPending}
              data-testid="btn-confirm-cancel"
            >
              {isPending ? 'Cancelando...' : 'Confirmar cancelamento'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ─── Promo Code Input (Batch P2) ─────────────────────────────────────────────

function PromoCodeSection({ bookingId, onApplied }: { bookingId: string; onApplied: () => void }) {
  const [code, setCode] = useState('')
  const apply = useApplyPromoCode()

  const handleApply = () => {
    if (!code.trim()) return
    apply.mutate(
      { code: code.trim(), bookingId },
      {
        onSuccess: () => {
          setCode('')
          onApplied()
        },
      },
    )
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Tag className="h-4 w-4 text-brand-600" />
          Código promocional
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-xs text-muted-foreground">
          Tem um código promocional? Aplique-o para obter desconto nesta aula.
        </p>
        <div className="flex gap-2">
          <Input
            placeholder="Ex: PROMO2026"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            onKeyDown={(e) => { if (e.key === 'Enter') handleApply() }}
            className="h-10 uppercase font-mono"
            data-testid="promo-code-booking-input"
          />
          <Button
            type="button"
            variant="outline"
            onClick={handleApply}
            disabled={apply.isPending || !code.trim()}
            className="shrink-0"
            data-testid="promo-code-booking-apply"
          >
            {apply.isPending
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : 'Aplicar'}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

// ─── Progress Diary ───────────────────────────────────────────────────────────

function ProgressDiary({ bookingId }: { bookingId: string }) {
  const key = `md-notes-${bookingId}`
  const [notes, setNotes] = useState(() => localStorage.getItem(key) ?? '')
  const [saved, setSaved] = useState(false)

  function handleSave() {
    localStorage.setItem(key, notes)
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-brand-600" />
          Diário de progresso
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Anote o que aprendeu nesta aula. As notas ficam salvas localmente no seu dispositivo.
        </p>
        <Textarea
          rows={4}
          placeholder="O que pratiquei, dificuldades, melhorias para a próxima aula..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={2000}
        />
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">{notes.length}/2000</p>
          <Button size="sm" variant="outline" onClick={handleSave}>
            {saved ? '✓ Salvo!' : 'Salvar notas'}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

// ─── Booking Detail Skeleton ──────────────────────────────────────────────────

function BookingDetailSkeleton() {
  return (
    <div className="space-y-5 max-w-2xl mx-auto">
      <Skeleton className="h-6 w-32" />
      <Skeleton className="h-28 rounded-xl" />
      <Skeleton className="h-20 rounded-xl" />
      <Skeleton className="h-72 rounded-xl" />
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function StudentBookingDetail() {
  const { bookingId } = useParams<{ bookingId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()

  const { data, isLoading, error, refetch } = useBooking(bookingId ?? '')
  const cancelMutation = useCancelBooking()

  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false)

  const { data: cancellationPreview } = useCancellationPreview(
    showCancelDialog ? (bookingId ?? '') : '',
  )
  const { data: invoiceData, isLoading: invoiceLoading } = useBookingInvoice(
    showInvoiceDialog ? bookingId : undefined,
  )
  const [showDisputeDialog, setShowDisputeDialog] = useState(false)
  const [reviewSubmitted, setReviewSubmitted] = useState(false)
  const [paymentBanner, setPaymentBanner] = useState<'success' | 'failure' | 'pending' | null>(null)
  const [showSosDialog, setShowSosDialog] = useState(false)
  const [sosSending, setSosSending] = useState(false)
  const [sosSent, setSosSent] = useState(false)

  const booking = data as BookingWithDetails | undefined

  const { data: existingReview } = useQuery({
    queryKey: ['booking-review', bookingId],
    queryFn: async () => {
      const { data: rv } = await supabase
        .from('reviews')
        .select('id')
        .eq('booking_id', bookingId!)
        .maybeSingle()
      return rv
    },
    enabled: Boolean(bookingId) && booking?.status === 'completed',
  })

  // Show banner when Mercado Pago redirects back with ?payment=success|failure|pending
  useEffect(() => {
    const paymentResult = searchParams.get('payment')
    const VALID = ['success', 'failure', 'pending'] as const
    if (!paymentResult || !VALID.includes(paymentResult as (typeof VALID)[number])) return
    setPaymentBanner(paymentResult as 'success' | 'failure' | 'pending')
    if (paymentResult === 'success' && bookingId) {
      track('checkout_completed', { booking_id: bookingId })
    }
    setSearchParams({}, { replace: true })
  }, [searchParams, setSearchParams, bookingId])

  // ── Error state ──────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="max-w-2xl mx-auto py-12 text-center">
        <p className="text-red-600 font-medium">Erro ao carregar aula.</p>
        <p className="text-muted-foreground text-sm mt-1">{error.message}</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/aluno/dashboard')}>
          Voltar ao dashboard
        </Button>
      </div>
    )
  }

  if (isLoading || !booking) return <BookingDetailSkeleton />

  const instructor = booking.instructor
  const instructorName = instructor?.profile?.full_name ?? 'Instrutor'
  const avatarUrl = instructor?.profile?.avatar_url ?? undefined
  const statusColor = getBookingStatusColor(booking.status)
  const statusLabel = getBookingStatusLabel(booking.status)

  const showReviewSection =
    booking.status === 'completed' && !reviewSubmitted && !existingReview

  async function handleCancel() {
    try {
      await cancelMutation.mutateAsync({
        bookingId: booking!.id,
        cancelledBy: 'student',
      })
      const refundMsg = cancellationPreview?.refund_amount && cancellationPreview.refund_amount > 0
        ? ` Reembolso de R$ ${cancellationPreview.refund_amount.toFixed(2).replace('.', ',')} será processado.`
        : ''
      toast.success(`Aula cancelada com sucesso.${refundMsg}`)
      setShowCancelDialog(false)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao cancelar aula.'
      toast.error(msg)
    }
  }

  // MÉDIO-001: escapa caracteres HTML para evitar XSS em document.write
  function escapeHtml(str: string): string {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')
  }

  function handlePrintReceipt() {
    const win = window.open('', '_blank', 'width=600,height=700')
    if (!win) return
    const date = format(parseISO(booking.scheduled_at), "d 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR })
    const safeInstructorName = escapeHtml(instructorName)
    const safeDate = escapeHtml(date)
    const safeDuration = escapeHtml(getDurationLabel(booking.duration_minutes))
    const safeBookingId = escapeHtml(booking.id.slice(0, 8))
    const safePickupAddress = booking.pickup_address ? escapeHtml(booking.pickup_address) : null
    const safeEmittedAt = escapeHtml(new Date().toLocaleDateString('pt-BR'))
    win.document.write(`
      <!DOCTYPE html>
      <html lang="pt-BR">
      <head>
        <meta charset="UTF-8" />
        <title>Recibo — MeuDriver #${safeBookingId}</title>
        <style>
          body { font-family: Arial, sans-serif; max-width: 540px; margin: 40px auto; color: #111; }
          h1 { font-size: 20px; margin-bottom: 4px; }
          .sub { color: #666; font-size: 13px; margin-bottom: 24px; }
          .divider { border: none; border-top: 1px solid #e5e7eb; margin: 16px 0; }
          .row { display: flex; justify-content: space-between; margin: 8px 0; font-size: 14px; }
          .label { color: #666; }
          .value { font-weight: 600; }
          .total { font-size: 16px; font-weight: 700; color: #0054b3; }
          .footer { margin-top: 40px; font-size: 11px; color: #999; text-align: center; }
          @media print { body { margin: 20px; } }
        </style>
      </head>
      <body>
        <h1>MeuDriver</h1>
        <p class="sub">Recibo de aula — #${safeBookingId}</p>
        <hr class="divider" />
        <div class="row"><span class="label">Instrutor</span><span class="value">${safeInstructorName}</span></div>
        <div class="row"><span class="label">Data e horário</span><span class="value">${safeDate}</span></div>
        <div class="row"><span class="label">Duração</span><span class="value">${safeDuration}</span></div>
        ${safePickupAddress ? `<div class="row"><span class="label">Local de partida</span><span class="value">${safePickupAddress}</span></div>` : ''}
        <hr class="divider" />
        ${booking.total_price != null ? `<div class="row"><span class="label total">Total pago</span><span class="value total">R$ ${booking.total_price.toFixed(2).replace('.', ',')}</span></div>` : ''}
        <hr class="divider" />
        <p class="footer">MeuDriver — Marketplace de instrutores de direção<br/>Emitido em ${safeEmittedAt}</p>
        <script>window.onload = () => window.print()</script>
      </body>
      </html>
    `)
    win.document.close()
  }

  async function handleSos() {
    setSosSending(true)
    try {
      const { error } = await supabase.rpc('notify_admins_sos', {
        p_booking_id: booking!.id,
        p_body: `Aula em andamento`,
      })
      if (error) throw error
      setSosSent(true)
      toast.success('Alerta enviado! Nossa equipe foi notificada.')
    } catch (err) {
      toast.error('Erro ao enviar alerta. Ligue para o suporte.')
    } finally {
      setSosSending(false)
      setShowSosDialog(false)
    }
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">

      {/* ── Payment result banner ───────────────────────────────────────────── */}
      {paymentBanner === 'success' && (
        <div className="flex items-start gap-3 rounded-xl bg-green-50 border border-green-200 px-4 py-4">
          <CheckCircle className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-green-800">Pagamento confirmado!</p>
            <p className="text-sm text-green-700 mt-0.5">Sua aula foi agendada com sucesso. Você receberá uma confirmação em breve.</p>
          </div>
          <button onClick={() => setPaymentBanner(null)} className="shrink-0 text-green-500 hover:text-green-700 text-lg leading-none">&times;</button>
        </div>
      )}
      {paymentBanner === 'failure' && (
        <div className="flex items-start gap-3 rounded-xl bg-red-50 border border-red-200 px-4 py-4">
          <XCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-red-800">Pagamento não aprovado</p>
            <p className="text-sm text-red-700 mt-0.5">Houve um problema com o pagamento. Verifique seus dados e tente novamente.</p>
          </div>
          <button onClick={() => setPaymentBanner(null)} className="shrink-0 text-red-400 hover:text-red-600 text-lg leading-none">&times;</button>
        </div>
      )}
      {paymentBanner === 'pending' && (
        <div className="flex items-start gap-3 rounded-xl bg-amber-50 border border-amber-200 px-4 py-4">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-800">Pagamento em análise</p>
            <p className="text-sm text-amber-700 mt-0.5">Seu pagamento está sendo processado. Você será notificado assim que confirmado.</p>
          </div>
          <button onClick={() => setPaymentBanner(null)} className="shrink-0 text-amber-500 hover:text-amber-700 text-lg leading-none">&times;</button>
        </div>
      )}

      {/* ── Back nav ───────────────────────────────────────────────────────── */}
      <button
        type="button"
        onClick={() => navigate('/aluno/dashboard')}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" />
        Voltar ao dashboard
      </button>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Detalhe da aula</h1>
          <p className="text-xs text-muted-foreground mt-0.5">#{booking.id.slice(0, 8)}</p>
        </div>
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${statusColor}`}>
          {statusLabel}
        </span>
      </div>

      {/* ── Instructor card ─────────────────────────────────────────────────── */}
      <Card>
        <CardContent className="p-5 flex items-center gap-4">
          <Avatar className="h-14 w-14">
            <AvatarImage src={avatarUrl} />
            <AvatarFallback className="bg-primary/10 text-primary text-lg font-semibold">
              {getInitials(instructorName)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900">{instructorName}</p>
            <p className="text-sm text-muted-foreground">Instrutor</p>
            {instructor?.profile?.phone && (
              <a
                href={`tel:${instructor.profile.phone}`}
                className="text-sm text-primary hover:underline mt-0.5 inline-block"
              >
                {instructor.profile.phone}
              </a>
            )}
          </div>
          {instructor?.slug && (
            <Link to={`/instrutores/${instructor.slug}`}>
              <Button variant="outline" size="sm">Ver perfil</Button>
            </Link>
          )}
        </CardContent>
      </Card>

      {/* ── Booking info ────────────────────────────────────────────────────── */}
      <Card>
        <CardContent className="p-5 space-y-3 text-sm">
          <div className="flex items-center gap-3 text-gray-700">
            <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
            <div>
              <p className="font-medium">Data e horário</p>
              <p className="text-muted-foreground">{formatDateTime(booking.scheduled_at)}</p>
            </div>
          </div>
          <Separator />
          <div className="flex items-center gap-3 text-gray-700">
            <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
            <div>
              <p className="font-medium">Duração</p>
              <p className="text-muted-foreground">{getDurationLabel(booking.duration_minutes)}</p>
            </div>
          </div>
          {booking.pickup_address && (
            <>
              <Separator />
              <div className="flex items-center gap-3 text-gray-700">
                <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                <div>
                  <p className="font-medium">Local de partida</p>
                  <p className="text-muted-foreground">{booking.pickup_address}</p>
                </div>
              </div>
            </>
          )}
          {booking.notes && (
            <>
              <Separator />
              <div className="text-gray-700">
                <p className="font-medium mb-0.5">Observações</p>
                <p className="text-muted-foreground">{booking.notes}</p>
              </div>
            </>
          )}
          {booking.total_price != null && (
            <>
              <Separator />
              <div className="flex justify-between font-semibold text-base">
                <span>Total pago</span>
                <span className="text-primary">{formatCurrency(booking.total_price)}</span>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* ── Status timeline ─────────────────────────────────────────────────── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Progresso da aula</CardTitle>
        </CardHeader>
        <CardContent>
          <StatusTimeline status={booking.status} />
        </CardContent>
      </Card>

      {/* ── Trajeto previsto (mini-mapa) ────────────────────────────────────── */}
      {(booking.status === 'confirmed' ||
        booking.status === 'in_progress' ||
        booking.status === 'completed') &&
        booking.instructor?.latitude != null &&
        booking.instructor?.longitude != null &&
        booking.instructor?.service_radius_km != null && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <MapPin className="h-4 w-4 text-orange-600" />
                Trajeto previsto
              </CardTitle>
            </CardHeader>
            <CardContent>
              <InstructorServiceAreaMap
                centerLat={booking.instructor.latitude}
                centerLng={booking.instructor.longitude}
                radiusKm={booking.instructor.service_radius_km}
                instructorName={booking.instructor.profile?.full_name ?? undefined}
                pickupLat={booking.pickup_lat}
                pickupLng={booking.pickup_lng}
                pickupAddress={booking.pickup_address}
                height={240}
              />
              <p className="mt-2 text-xs text-muted-foreground">
                Área de atendimento do instrutor (raio de{' '}
                {booking.instructor.service_radius_km} km) com seu ponto de embarque.
              </p>
            </CardContent>
          </Card>
        )}

      {/* ── Chat ────────────────────────────────────────────────────────────── */}
      {user && (
        <ChatSection bookingId={booking.id} currentUserId={user.id} />
      )}

      {/* ── Review section ──────────────────────────────────────────────────── */}
      {showReviewSection && user && (
        <ReviewSection
          booking={booking}
          studentId={user.id}
          onReviewSubmitted={() => {
            setReviewSubmitted(true)
            refetch()
          }}
        />
      )}

      {reviewSubmitted && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-4 flex items-center gap-3">
            <CheckCircle className="h-5 w-5 text-green-600 shrink-0" />
            <p className="text-sm text-green-700 font-medium">
              Avaliação enviada! Obrigado pelo seu feedback.
            </p>
          </CardContent>
        </Card>
      )}

      {/* ── Código promocional (P2) — disponível para bookings pendentes ──── */}
      {booking.status === 'pending' && (
        <PromoCodeSection bookingId={booking.id} onApplied={refetch} />
      )}

      {/* ── Progress diary ──────────────────────────────────────────────────── */}
      {booking.status === 'completed' && (
        <ProgressDiary bookingId={booking.id} />
      )}

      {/* ── Instructor note ─────────────────────────────────────────────────── */}
      {booking.status === 'completed' && booking.instructor_note && (
        <Card className="border-teal-100 bg-teal-50/40">
          <CardContent className="p-4 space-y-1.5">
            <p className="text-xs font-semibold text-teal-700 flex items-center gap-1.5">
              <FileText className="h-3.5 w-3.5" />
              Nota do instrutor
            </p>
            <p className="text-sm text-gray-700 leading-relaxed">{booking.instructor_note}</p>
          </CardContent>
        </Card>
      )}

      {/* ── SOS Button — apenas quando aula está em andamento ───────────────── */}
      {booking.status === 'in_progress' && !sosSent && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-4 flex items-center justify-between gap-4">
          <div>
            <p className="font-semibold text-red-800 text-sm">Precisa de ajuda urgente?</p>
            <p className="text-xs text-red-600 mt-0.5">
              Use o botao SOS para alertar nossa equipe imediatamente.
            </p>
          </div>
          <Button
            type="button"
            className="bg-red-600 hover:bg-red-700 text-white shrink-0 gap-2"
            onClick={() => setShowSosDialog(true)}
            data-testid="sos-btn"
          >
            <AlertTriangle className="h-4 w-4" />
            SOS
          </Button>
        </div>
      )}
      {sosSent && (
        <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800 flex items-center gap-2">
          <CheckCircle className="h-4 w-4 shrink-0" />
          Alerta SOS enviado. Nossa equipe foi notificada.
        </div>
      )}

      {/* ── Actions ─────────────────────────────────────────────────────────── */}
      <div className="pb-4 flex flex-wrap gap-3">
        {canCancel(booking) && (
          <Button
            variant="outline"
            className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
            onClick={() => setShowCancelDialog(true)}
          >
            <XCircle className="h-4 w-4 mr-2" />
            Cancelar aula
          </Button>
        )}
        {['completed', 'cancelled_by_instructor', 'no_show'].includes(booking.status) && (
          <Button
            variant="outline"
            className="text-orange-600 border-orange-200 hover:bg-orange-50 hover:text-orange-700"
            onClick={() => setShowDisputeDialog(true)}
          >
            <Flag className="h-4 w-4 mr-2" />
            Abrir disputa
          </Button>
        )}
        {booking.status === 'completed' && (
          <Button
            variant="outline"
            className="text-gray-600 border-gray-200 hover:bg-gray-50"
            onClick={handlePrintReceipt}
          >
            <Printer className="h-4 w-4 mr-2" />
            Imprimir recibo
          </Button>
        )}
        {booking.status === 'completed' && (
          <Button
            variant="outline"
            className="text-blue-600 border-blue-200 hover:bg-blue-50 hover:text-blue-700"
            onClick={() => setShowInvoiceDialog(true)}
            data-testid="btn-ver-recibo"
          >
            <FileText className="h-4 w-4 mr-2" />
            Ver Recibo
          </Button>
        )}
        {['confirmed', 'in_progress'].includes(booking.status) && (
          <Button
            variant="outline"
            className="text-blue-600 border-blue-200 hover:bg-blue-50 hover:text-blue-700"
            onClick={() => {
              const ics = generateICS({
                title: `Aula de direção — ${instructorName}`,
                description: `Aula com ${instructorName} via MeuDriver`,
                location: booking.pickup_address ?? '',
                startAt: parseISO(booking.scheduled_at),
                durationMinutes: booking.duration_minutes,
              })
              downloadICS(`aula-meudriver-${booking.id.slice(0, 8)}.ics`, ics)
            }}
          >
            <CalendarPlus className="h-4 w-4 mr-2" />
            Adicionar ao calendário
          </Button>
        )}
        {booking.status === 'confirmed' && (
          <Button
            variant="outline"
            className="text-brand-600 border-brand-200 hover:bg-brand-50 hover:text-brand-700"
            onClick={async () => {
              try {
                const { error } = await supabase.functions.invoke('sync-calendar-event', {
                  body: { booking_id: booking.id, action: 'create' },
                })
                if (error) throw new Error(error.message)
                toast.success('Aula adicionada ao Google Calendar!')
              } catch (err) {
                console.error('[StudentBookingDetail] gcal sync error:', err)
                toast.error('Erro ao sincronizar com o Google Calendar. Verifique a conexão nas configurações do perfil.')
              }
            }}
            data-testid="gcal-sync-btn"
          >
            <CalendarCheck className="h-4 w-4 mr-2" />
            Adicionar ao Google Calendar
          </Button>
        )}
      </div>

      {/* ── O3: Painel de reagendamento por solicitação ──────────────────────── */}
      {['confirmed', 'pending_payment'].includes(booking.status) && user && (
        <div className="mt-4">
          <RescheduleRequestPanel
            bookingId={booking.id}
            currentUserId={user.id}
            studentId={booking.student_id}
            instructorProfileId={booking.instructor.profile_id}
            canRequest={canReschedule(booking)}
          />
        </div>
      )}

      {/* ── Cancel dialog ───────────────────────────────────────────────────── */}
      <CancelDialog
        open={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        onConfirm={handleCancel}
        isPending={cancelMutation.isPending}
        refundAmount={cancellationPreview?.refund_amount}
        refundPercentage={cancellationPreview?.refund_percentage}
        policyName={cancellationPreview?.policy_name}
      />

      {/* ── Dispute dialog ──────────────────────────────────────────────────── */}
      {user && (
        <DisputeDialog
          open={showDisputeDialog}
          onClose={() => setShowDisputeDialog(false)}
          bookingId={booking.id}
          studentId={user.id}
          instructorId={booking.instructor_id}
        />
      )}

      {/* ── Invoice dialog ──────────────────────────────────────────────────── */}
      <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <FileText className="h-5 w-5" />
              Recibo / Fatura
            </DialogTitle>
          </DialogHeader>
          {invoiceLoading ? (
            <InvoiceDocumentSkeleton />
          ) : invoiceData ? (
            <InvoiceDocument data={invoiceData} />
          ) : (
            <p className="text-sm text-muted-foreground py-4 text-center">
              Nao foi possivel carregar os dados da fatura. Tente novamente.
            </p>
          )}
        </DialogContent>
      </Dialog>

      {/* ── SOS dialog ──────────────────────────────────────────────────────── */}
      <Dialog open={showSosDialog} onOpenChange={setShowSosDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" />
              Pedir ajuda — SOS
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              Ao confirmar, nossa equipe sera alertada imediatamente sobre sua situacao.
              Use este recurso apenas em situacoes de emergencia ou desconforto real.
            </p>
            <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-800">
              Voce tem certeza? Isso alertara nossa equipe agora.
            </div>
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button
              variant="outline"
              onClick={() => setShowSosDialog(false)}
              disabled={sosSending}
            >
              Cancelar
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 gap-2"
              onClick={handleSos}
              disabled={sosSending}
              data-testid="sos-confirm-btn"
            >
              {sosSending ? (
                <><AlertTriangle className="h-4 w-4 animate-pulse" />Enviando...</>
              ) : (
                <><AlertTriangle className="h-4 w-4" />Confirmar SOS</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  )
}
